/**
 * O campo de texto ("area") de uma ficha do mundo, com o comentário por trecho.
 *
 * <p>A edição do campo não muda: mesmo `<textarea>`, mesmo "grava ao sair dele". O que
 * se soma é a seleção — quem marca um pedaço do texto ganha um "Comentar" que guarda o
 * trecho como citação e abre uma conversa por cima da página, sem grifo dentro do texto
 * e sem mexer no que já funciona.
 *
 * <p><b>O balão nasce onde o mouse soltou a seleção</b>, não embaixo do campo. A
 * primeira versão desenhava a barra no fluxo normal, logo após o `<textarea>` — e num
 * campo alto, ou perto do fim da tela, ela saía da vista de quem tinha acabado de
 * selecionar: "selecionei o texto mas não achei onde clicar" foi o retorno exato. Um
 * balão fixo, ancorado no ponto do clique, é o padrão que qualquer editor de seleção de
 * texto já ensina — não tem para onde a pessoa precisar procurar.
 *
 * <p>Não há âncora de posição no <b>texto</b>: o trecho é citação, não coordenada de
 * caractere. Editar o campo depois não move nem invalida o comentário — ele continua
 * contando o que foi dito, como a legenda de uma citação continua valendo mesmo que o
 * livro ganhe uma nova edição.
 */
import { useEffect, useRef, useState } from 'react';

import { Avatar } from '../../comum/Avatar';
import { tempoRelativo } from '../../comum/tempoRelativo';
import { TextoRico } from '../TextoRico';
import {
  useComentariosDoMundo,
  useMutacoesComentarioDoMundo,
} from './comentariosDoTextoApi';
import type { Colecao } from './tipos';

interface Selecao {
  trecho: string;
  /** Onde o balão nasce — o ponto do clique/soltura, em coordenadas de viewport. */
  x: number;
  y: number;
}

const CITADO_ATE = 80;
const citar = (trecho: string) =>
  trecho.length > CITADO_ATE ? `${trecho.slice(0, CITADO_ATE - 1)}…` : trecho;

/** Larguras aproximadas do balão fechado e aberto — o suficiente para não deixar a
 *  caixa nascer meio pra fora da tela numa seleção perto da borda. */
const LARGURA_BARRA = 260;
const LARGURA_FORM = 300;
const ALTURA_ESTIMADA = 140;
const MARGEM = 12;

function ancorar(x: number, y: number, largura: number) {
  const maxX = window.innerWidth - largura - MARGEM;
  const maxY = window.innerHeight - ALTURA_ESTIMADA - MARGEM;
  return {
    left: Math.min(Math.max(x, MARGEM), Math.max(maxX, MARGEM)),
    top: Math.min(Math.max(y + 10, MARGEM), Math.max(maxY, MARGEM)),
  };
}

export function CampoDeAreaComComentarios({
  colecao,
  fichaId,
  campoChave,
  valor,
  larga,
  aoAplicar,
}: {
  colecao: Colecao;
  fichaId: number;
  campoChave: string;
  valor: string;
  larga?: boolean;
  aoAplicar: (valor: string) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const balaoRef = useRef<HTMLDivElement>(null);
  const [selecao, setSelecao] = useState<Selecao | null>(null);
  const [escrevendo, setEscrevendo] = useState(false);
  const [rascunho, setRascunho] = useState('');
  const [listaAberta, setListaAberta] = useState(false);

  const { data: todosOsComentarios } = useComentariosDoMundo(colecao, fichaId);
  const { criar, resolver, excluir } = useMutacoesComentarioDoMundo(colecao, fichaId);

  const comentarios = (todosOsComentarios ?? []).filter((c) => c.campo === campoChave);
  const abertos = comentarios.filter((c) => !c.resolvidoEm).length;

  function fecharBalao() {
    setSelecao(null);
    setEscrevendo(false);
  }

  /** Seleção pelo mouse: o balão nasce exatamente onde o botão foi solto. */
  function aoSoltarMouse(e: React.MouseEvent<HTMLTextAreaElement>) {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart, selectionEnd, value } = el;
    if (selectionEnd > selectionStart) {
      setSelecao({ trecho: value.slice(selectionStart, selectionEnd), x: e.clientX, y: e.clientY });
    } else {
      fecharBalao();
    }
  }

  /** Seleção pelo teclado (Shift+seta): sem coordenada de clique, ancora perto do
   *  canto do campo — ainda visível, mesmo sem ter vindo de um clique. */
  function aoSoltarTecla() {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart, selectionEnd, value } = el;
    if (selectionEnd > selectionStart) {
      const rect = el.getBoundingClientRect();
      setSelecao({
        trecho: value.slice(selectionStart, selectionEnd),
        x: rect.left + 16,
        y: rect.top + 16,
      });
    } else {
      fecharBalao();
    }
  }

  // Clicar fora do balão (e fora do próprio campo, que continua editável por baixo)
  // fecha a seleção pendente — sem isso, um balão de uma seleção esquecida ficaria
  // flutuando sobre a tela para sempre.
  useEffect(() => {
    if (!selecao) return;
    function aoClicarFora(e: MouseEvent) {
      const alvo = e.target as Node;
      if (balaoRef.current?.contains(alvo) || textareaRef.current?.contains(alvo)) return;
      fecharBalao();
    }
    function aoRolar() {
      fecharBalao();
    }
    document.addEventListener('mousedown', aoClicarFora);
    // `capture: true` pega o scroll de qualquer contêiner rolável entre o campo e a
    // janela, não só o da janela — a posição do balão é `fixed`, então rolar qualquer
    // um deles o deixaria apontando para o lugar errado.
    window.addEventListener('scroll', aoRolar, { capture: true });
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      window.removeEventListener('scroll', aoRolar, { capture: true });
    };
  }, [selecao]);

  function enviar() {
    if (!selecao || !rascunho.trim()) return;
    criar.mutate(
      { campo: campoChave, trecho: selecao.trecho, texto: rascunho.trim() },
      {
        onSuccess: () => {
          setRascunho('');
          setEscrevendo(false);
          setSelecao(null);
          setListaAberta(true);
        },
      }
    );
  }

  return (
    <div className="cx">
      <textarea
        ref={textareaRef}
        className="fi-area"
        rows={larga ? 5 : 3}
        defaultValue={valor}
        onBlur={(e) => {
          if (e.target.value !== valor) aoAplicar(e.target.value);
        }}
        onMouseUp={aoSoltarMouse}
        onKeyUp={(e) => {
          if (e.key.startsWith('Arrow') || e.key === 'Shift') aoSoltarTecla();
        }}
      />

      {selecao && !escrevendo && (
        <div
          ref={balaoRef}
          className="cx-balao cx-barra"
          style={ancorar(selecao.x, selecao.y, LARGURA_BARRA)}
        >
          <span className="cx-barra-trecho">“{citar(selecao.trecho)}”</span>
          <button type="button" className="cx-btn" autoFocus onClick={() => setEscrevendo(true)}>
            💬 Comentar
          </button>
          <button type="button" className="cx-btn-txt" onClick={fecharBalao}>
            Cancelar
          </button>
        </div>
      )}

      {selecao && escrevendo && (
        <div
          ref={balaoRef}
          className="cx-balao cx-form"
          style={ancorar(selecao.x, selecao.y, LARGURA_FORM)}
        >
          <div className="cx-form-trecho">sobre “{citar(selecao.trecho)}”</div>
          <textarea
            className="cx-form-txt"
            rows={2}
            autoFocus
            placeholder="Sugestão de mudança, ou o que chamou atenção…"
            value={rascunho}
            onChange={(e) => setRascunho(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') fecharBalao();
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) enviar();
            }}
          />
          <div className="cx-form-acoes">
            <button
              type="button"
              className="cx-btn"
              disabled={!rascunho.trim() || criar.isPending}
              onClick={enviar}
            >
              {criar.isPending ? 'enviando…' : 'Enviar'}
            </button>
            <button type="button" className="cx-btn-txt" onClick={fecharBalao}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {comentarios.length > 0 && (
        <button
          type="button"
          className={`cx-selo${abertos > 0 ? ' com-abertos' : ''}`}
          onClick={() => setListaAberta((a) => !a)}
        >
          💬 {comentarios.length}
          {abertos > 0 ? ` · ${abertos} em aberto` : ' · tudo resolvido'}
          <span className="cx-selo-seta">{listaAberta ? '▴' : '▾'}</span>
        </button>
      )}

      {listaAberta && comentarios.length > 0 && (
        <div className="cx-lista">
          {comentarios.map((c) => (
            <div key={c.id} className={`cx-item${c.resolvidoEm ? ' resolvido' : ''}`}>
              <Avatar
                className="cx-av"
                nome={c.autorNome}
                cor={c.autorCor}
                url={c.autorAvatar}
                title={c.autorNome ?? 'usuário removido'}
              />
              <div className="cx-corpo">
                <div className="cx-trecho">“{citar(c.trecho)}”</div>
                <div className="cx-topo">
                  <b className={c.autorNome ? '' : 'cx-sem-autor'}>
                    {c.autorNome ?? 'usuário removido'}
                  </b>
                  <span className="cx-quando">{tempoRelativo(c.criadoEm)}</span>
                </div>
                <div className="cx-texto">
                  <TextoRico texto={c.texto} membros={[]} />
                </div>
                <div className="cx-acoes">
                  {!c.resolvidoEm && (
                    <button onClick={() => resolver.mutate({ id: c.id, resolvido: true })}>
                      marcar resolvido
                    </button>
                  )}
                  {c.resolvidoEm && (
                    <span className="cx-resolvido-selo">
                      resolvido{c.resolvidoPorNome ? ` por ${c.resolvidoPorNome}` : ''}
                    </span>
                  )}
                  {c.resolvidoEm && (
                    <button onClick={() => resolver.mutate({ id: c.id, resolvido: false })}>
                      reabrir
                    </button>
                  )}
                  {c.meu && (
                    <button
                      className="perigo"
                      onClick={() => {
                        if (confirm('Apagar este comentário?')) excluir.mutate(c.id);
                      }}
                    >
                      apagar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
